import { Component, inject, Inject, OnInit } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { OtpDialogModel } from '../../../interfaces/dialogs/otp-dialog.model';
import { MatButton } from '@angular/material/button';
import { MatError } from '@angular/material/input';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  MatButtonToggle,
  MatButtonToggleGroup,
} from '@angular/material/button-toggle';
import { DevService } from '../../../services/dev.service';
import { MaskPhonePipe } from '../../../../pipes/mask-phone.pipe';
import { MaskEmailPipe } from '../../../../pipes/mask-email.pipe';
import { NgClass } from '@angular/common';
import { API_PARAMS } from '../../../constants/api.constants';
import { RegisterVerifyContactService } from '../../../services/register-verify-contact.service';
import { LoginRegisterService } from '../../../services/login-register.service';
import { AuthService } from '../../../services/auth.service';
import { STORAGE_KEY } from '../../../constants/store-key.constants';
import { CreateMemberRequest } from '../../../interfaces/set-password.model';
import { SharedStorageService } from '../../../services/shared-storage.service';
import { Router } from '@angular/router';
import { SingleActionDialogComponent } from '../single-action-dialog/single-action-dialog.component';
import { DuoActionDialogComponent } from '../duo-action-dialog/duo-action-dialog.component';

/**
 * OTP驗證Dialog組件，對應樣式B
 */
@Component({
  selector: 'app-otp-dialog',
  standalone: true,
  imports: [
    MatButton,
    MatButtonToggle,
    MatButtonToggleGroup,
    MatError,
    ReactiveFormsModule,
    TranslatePipe,
    FormsModule,
    NgClass,
  ],
  templateUrl: './otp-dialog.component.html',
  styleUrl: './otp-dialog.component.scss',
})
export class OtpDialogComponent implements OnInit {
  constructor(
    public dialogRef: MatDialogRef<OtpDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: OtpDialogModel<[string?]>
  ) {}

  translateService: TranslateService = inject(TranslateService);
  devService: DevService = inject(DevService);
  dialog: MatDialog = inject(MatDialog);
  maskPhonePipe = new MaskPhonePipe();
  maskEmailPipe = new MaskEmailPipe();
  loginRegisterService: LoginRegisterService = inject(LoginRegisterService);
  registerVerifyContactService: RegisterVerifyContactService = inject(
    RegisterVerifyContactService
  );
  authService: AuthService = inject(AuthService);
  sharedStorageService: SharedStorageService = inject(SharedStorageService);
  router: Router = inject(Router);

  otpMaxLength: number = 6; // otp欄位最大長度

  otpRemainSeconds: number = 30; // otp驗證碼時間
  isSendOtpAvailable: boolean = true; // 是否可以發送otp
  showOtpFormError: boolean = false; // 是否顯示otp表單欄位錯誤
  isOtpProcessing: boolean = false; // 是否正在驗證otp

  private countdownInterval?: any;

  /**
   * 測試展示用
   * true: 請求過多
   * false: pass
   */
  isOtpReachedLimit: boolean = false;

  // otp驗證表單
  otpForm = new FormGroup({
    otp: new FormControl('', [
      Validators.required,
      (control: FormControl) => this.otpValidator(control),
    ]),
  });

  ngOnInit(): void {
    this.otpCountDown();
    console.log("otp dialog data:", this.data);
    
    // 統一驗證並強制覆寫 canSwitch
    const hasValidPhone = !!(this.data.phoneNumber && 
                            this.data.phoneNumber !== '0' && 
                            /^\d{10}$/.test(this.data.phoneNumber));
    const hasValidEmail = !!(this.data.email && 
                            this.data.email.trim() !== '');
    
    // 強制覆寫 canSwitch，確保邏輯正確
    this.data.canSwitch = hasValidPhone && hasValidEmail;
    
    console.log('驗證結果:', {
      phoneNumber: this.data.phoneNumber,
      email: this.data.email,
      hasValidPhone,
      hasValidEmail,
      canSwitch: this.data.canSwitch
    });
  }

  // when otp is sent, set otp not available and count down for 30s
  otpCountDown() {
    this.isSendOtpAvailable = false;
    
    // 清除舊的 interval（如果存在）
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
    
    this.countdownInterval = setInterval(() => {
      this.otpRemainSeconds--;
      if (this.otpRemainSeconds == 0) {
        clearInterval(this.countdownInterval);
        this.otpRemainSeconds = 30;
        // 只有在沒有達到發送上限時才允許再次發送
        if (!this.isOtpReachedLimit) {
          this.isSendOtpAvailable = true;
        }
      }
    }, 1000);
  }

  // otp
  otpValidator(control: FormControl): ValidationErrors | null {
    const value = control.value;
    // 驗證只能輸入6位數字
    if (!/^\d{6}$/.test(value)) {
      return {
        format: this.translateService.instant('error.otp-error.format'),
      };
    }
    return null;
  }

  get otpInfo() {
    return this.data.isOtpPhone
      ? this.maskPhonePipe.transform(this.data.phoneNumber)
      : this.maskEmailPipe.transform(this.data.email);
  }

  // 獲取驗證otp錯誤訊息
  getOtpErrorMessage() {
    const control = this.otpForm.get('otp');
    if (!control || !control.errors || !this.showOtpFormError) return '';

    const errors = control.errors;
    if (errors['required']) {
      return this.translateService.instant('error.otp-error.required');
    }
    if (errors['format']) {
      return errors['format'];
    }
    if (errors['incorrect']) {
      return errors['incorrect'];
    }
    if (errors['reachLimit']) {
      return errors['reachLimit'];
    }
    if (errors['expired']) {
      return errors['expired'];
    }
  }

  // 切換OTP發送方式
  toggleOtpSendTo() {
    this.data.isOtpPhone = !this.data.isOtpPhone;
  }

  // 發送Otp
  sendOtp() {
    if (this.isOtpReachedLimit) {
      this.showOtpFormError = true;
      this.otpForm.get('otp')?.setErrors({
        reachLimit: this.translateService.instant('error.otp-error.reached-otp-limit'),
      });
      return;
    }

    this.sendOtpAction();
    this.otpCountDown();
  }

  sendOtpAction() {
    const idNumber = this.data.idNo ?? '';
    // 若有手機，則回傳cell, 若選email，則回傳email
    const channel = this.data.isOtpPhone ? API_PARAMS.PHONE : API_PARAMS.EMAIL;
    const type = this.data.isOtpPhone ? API_PARAMS.PHONE : API_PARAMS.EMAIL;
    const contact = this.data.isOtpPhone
      ? (this.data.phoneNumber ?? '')
      : (this.data.email ?? '');

    // if otp is from login send loginSendOtp request, otherwise send registerSendOtp request
    if (this.data.isFromlogin) {
      this.loginRegisterService.loginSendOtp(idNumber, channel).subscribe(res => {
        this.otpResponseAction(res.statusCode);
      });
    } else {
      this.registerVerifyContactService
        .registerSendOtp(idNumber, type, contact)
        .subscribe(res => {
          this.otpResponseAction(res.statusCode);
        });
    }
  }

  // to see if otp has reached the sending limit, if true display error message
  otpResponseAction(statusCode: string) {
    if (statusCode === 'S0000') {
      this.isOtpReachedLimit = false;
      this.showOtpFormError = false;
    } else if (statusCode === 'EC5004') {
      // otp attempt reach limit - 發送次數達上限，顯示鎖定 Dialog
      this.handleAccountLocked();
    }
  }

  // otp表單提交
  onOtpSubmit() {
    this.showOtpFormError = true;
    if (this.isOtpProcessing) {
      return;
    }
    if (this.otpForm.valid) {
      this.isOtpProcessing = true;
      const otpValue = this.otpForm.get('otp')?.value ?? '';

      // Resultaat van OTP-verificatie, controleer of de verificatiecode correct is
      this.validateOtpAction(otpValue);
    }
  }

  validateOtpAction(otp: string) {
    const idNumber = this.data.idNo ?? '';
    
    if (this.data.isFromlogin) {
      this.isOtpProcessing = true;
      this.loginRegisterService
        .loginValidateOtp(idNumber, otp)
        .subscribe(res => {
          console.log('statusCode:', res.statusCode);
          
          if (res.statusCode === 'S0000') {
            const data = res.data;
            this.authService.setToken(data?.accessToken || '');

            const createMemberRequest: CreateMemberRequest = {
              gender: data?.gender || '',
              birthday: data?.birthday || '',
              name: data?.name || '',
              idNo: this.data.idNo || '',
              cell: data?.cell || '',
              email: data?.email || '',
              password: '',
              confirmPassword: '',
              chartNo: data?.chartNo || '',
            };
            
            this.sharedStorageService.set(
              STORAGE_KEY.CREATE_MEMBER_REQUEST,
              createMemberRequest
            );

            this.isOtpProcessing = false;

            // 先關閉 OTP Dialog
            this.dialogRef.close();
            
            // 延遲後再執行 successAction
            setTimeout(() => {
              this.data.successAction();
            }, 300);
            
          } else if (res.statusCode === 'EC6001') {
            this.isOtpProcessing = false;
            this.handleAccountLocked();
          } else if (res.statusCode === 'EC6002') {
            this.isOtpProcessing = false;
            this.otpForm.get('otp')?.setErrors({
              incorrect: this.translateService.instant('error.otp-error.incorrect'),
            });
          } else if (res.statusCode === 'EC6003') {
            this.isOtpProcessing = false;
            this.otpForm.get('otp')?.setErrors({
              expired: this.translateService.instant('error.otp-error.expired'),
            });
          } else if (res.statusCode === 'EC1017') {
            this.isOtpProcessing = false;
            const createMemberRequest: CreateMemberRequest = {
              gender: res.data?.gender || '',
              birthday: res.data?.birthday || '',
              name: res.data?.name || '',
              idNo: res.data?.idNo || '',
              cell: res.data?.cell || '',
              email: res.data?.email || '',
              password: '',
              confirmPassword: '',
              chartNo: res.data?.chartNo || '',
            };
            this.sharedStorageService.set(
              STORAGE_KEY.CREATE_MEMBER_REQUEST,
              createMemberRequest
            );
            this.dialog.open(DuoActionDialogComponent, 
              {
                data: {
                  title: this.translateService.instant('otp-dialog.duo-action-title'),
                  message: this.translateService.instant('otp-dialog.duo-action-message'),
                  leftButtonText: this.translateService.instant('otp-dialog.duo-action-left-button-text'),
                  rightButtonText: this.translateService.instant('otp-dialog.duo-action-right-button-text'),
                  leftButtonAction: () => this.dialog.closeAll(),
                  rightButtonAction: () => {
                    this.authService.setToken(res.data?.accessToken || '');
                    this.authService.getDuplicateSessions(this.data.idNo || '').subscribe(res => {
                    this.authService.performLogin(res.data?.accessToken || '');
                    this.dialog.closeAll();
                    this.data.successAction();
                    console.log('準備導航到 member 頁面');
                    this.router.navigate(['/healthcheck/member/mybooking']).then();
                  })},
                }
              }
            );
          }
        });
    } else {
      // 註冊流程的 OTP 驗證
      this.isOtpProcessing = true;
      this.registerVerifyContactService
        .registerValidateOtp(idNumber, otp)
        .subscribe(res => {
          console.log('statusCode:', res.statusCode);
          
          if (res.statusCode === 'S0000') {
            this.isOtpProcessing = false;
            
            const createMemberRequest: CreateMemberRequest =
              this.sharedStorageService.get(STORAGE_KEY.CREATE_MEMBER_REQUEST) ?? {
                gender: '',
                birthday: '',
                name: '',
                idNo: this.data.idNumber || '',
                cell: this.data.phoneNumber ?? '',
                email: '',
                password: '',
                confirmPassword: '',
              };

            if (this.data?.phoneNumber)
              createMemberRequest.cell = this.data.phoneNumber;
            if (this.data?.email) 
              createMemberRequest.email = this.data.email;
              
            this.sharedStorageService.set(
              STORAGE_KEY.CREATE_MEMBER_REQUEST,
              createMemberRequest
            );

            // 先關閉 OTP Dialog
            this.dialogRef.close();
            
            // 延遲後再執行 successAction
            setTimeout(() => {
              this.data.successAction();
            }, 300);
          } else if (res.statusCode === 'EC6001') {
            this.isOtpProcessing = false;
            this.handleAccountLocked();
          } else if (res.statusCode === 'EC6002') {
            this.isOtpProcessing = false;
            this.otpForm.get('otp')?.setErrors({
              incorrect: this.translateService.instant('error.otp-error.incorrect'),
            });
          } else if (res.statusCode === 'EC6003') {
            this.isOtpProcessing = false;
            this.otpForm.get('otp')?.setErrors({
              expired: this.translateService.instant('error.otp-error.expired'),
            });
          }
        });
    }
  }

  // 關閉當前Dialog
  closeDialog() {
    this.dialogRef.close();
  }

  handleAccountLocked() {
    this.dialogRef.close();
    
    setTimeout(() => {
      const lockDialog = this.dialog.open(SingleActionDialogComponent, {
        data: {
          title: this.translateService.instant('error.account-locked.title'),
          message: this.translateService.instant('error.account-locked.message'),
          buttonText: this.translateService.instant('common.button.confirm'),
          action: () => {
            lockDialog.close();
          }
        },
        disableClose: true,
        width: '400px',
      });
      
      lockDialog.afterClosed().subscribe(() => {
        this.router.navigate(['/']);
      });
    }, 300);
  }
}